import React, { useState } from "react"
import type { StepConfig, TransformOperation, VariableGroups, VariableHint } from "./types"
import { INPUT_CLASS } from "./constants"
import { VariableBrowser } from "./VariableBrowser"

interface TransformConfigProps {
  config: StepConfig
  onChange: (updates: Partial<StepConfig>) => void
  disabled?: boolean
  variableHints?: VariableGroups
}

function formatOperations(operations: TransformOperation[] | undefined) {
  return JSON.stringify(operations || [], null, 2)
}

export function TransformConfig({ config, onChange, disabled = false, variableHints }: TransformConfigProps) {
  const [operationsText, setOperationsText] = useState(() => formatOperations(config.operations))
  const [operationsError, setOperationsError] = useState<string | null>(null)

  const changeOperations = (value: string) => {
    setOperationsText(value)
    try {
      const operations = JSON.parse(value)
      if (!Array.isArray(operations)) throw new Error("Operations must be a JSON array")
      onChange({ operations })
      setOperationsError(null)
    } catch (error) {
      setOperationsError(error instanceof Error ? error.message : "Operations must be valid JSON")
    }
  }

  return (
    <fieldset disabled={disabled} className="space-y-3 disabled:opacity-60">
      <VariableBrowser
        groups={variableHints}
        onInsert={(hint: VariableHint) => onChange({ input: { type: "context_pointer", path: hint.path } })}
      />
      <div className="space-y-1">
        <label className="block text-xs font-medium text-gray-700">Input JSON Pointer</label>
        <input
          value={config.input?.path || ""}
          onChange={(event) => onChange({ input: { type: "context_pointer", path: event.target.value } })}
          className={INPUT_CLASS}
          placeholder="/request/body"
        />
        <p className="text-xs text-gray-500">The transform reads this JSON-compatible value from the execution context.</p>
      </div>
      <div className="space-y-1">
        <label className="block text-xs font-medium text-gray-700">Operations (JSON)</label>
        <textarea
          aria-label="Transform operations JSON"
          value={operationsText}
          onChange={(event) => changeOperations(event.target.value)}
          rows={7}
          className={`${INPUT_CLASS} font-mono ${operationsError ? "border-red-300" : ""}`}
          spellCheck={false}
        />
        {operationsError ? (
          <p className="text-xs text-red-600" role="alert">{operationsError}</p>
        ) : (
          <p className="text-xs text-gray-500">Supported operations: select, set, remove, rename, pick, and wrap. Up to 50 operations.</p>
        )}
      </div>
    </fieldset>
  )
}
